import { GRID_BASE_M, metersToPixels } from '../sim/constants.ts';
import { DEFAULT_GRID_ALPHA, DEFAULT_GRID_COLOR } from '../sim/map/look.ts';

/**
 * The editor floor is far below the play floor on purpose: authoring an 8192-unit map means
 * needing to see all of it at once, while a racer who can see the whole course loses the
 * navigation problem the game is built around.
 */
export const EDITOR_MIN_ZOOM = 0.025;
export const PLAY_MIN_ZOOM = 0.1;
export const MAX_ZOOM = 4;
export const WHEEL_ZOOM_IN = 1.1;
export const WHEEL_ZOOM_OUT = 0.9;
/** One grid cell (the scale bar) reads this many metres when the editor first opens. */
export const EDITOR_DEFAULT_SCALE_M = 50;
/** Zoom 1 is 10 m per cell; this is the zoom that makes the scale bar read 50 m. */
export const EDITOR_DEFAULT_ZOOM = GRID_BASE_M / EDITOR_DEFAULT_SCALE_M;
export const GRID_STROKE_COLOR = DEFAULT_GRID_COLOR;
export const GRID_STROKE_ALPHA = DEFAULT_GRID_ALPHA;
export const GRID_SCREEN_PX = metersToPixels(GRID_BASE_M);

/** Outside the map rectangle the camera is void; open water is `waterAppearance()`. */
export const VOID_COLOR = 0x000000;

/**
 * Converts a screen-pixel measurement into world units at the given zoom. Strokes, handles and
 * labels are authored in screen pixels so they stay legible across the whole zoom range instead
 * of vanishing when zoomed out or ballooning when zoomed in.
 */
export function screenToWorld(screenPx: number, zoom: number): number {
  return screenPx / zoom;
}

export type WorldRect = { x: number; y: number; width: number; height: number };

/**
 * The world rectangle a camera with this scroll, size, and zoom is looking at.
 *
 * Phaser's `Camera.worldView` uses the same formula, but it is only written in `preRender`.
 * Drawing from `update` against that rectangle paints last frame's view, which is too small
 * the moment the player zooms out and leaves a void sliver flashing at the edges.
 */
export function cameraWorldRect(
  scrollX: number,
  scrollY: number,
  viewportWidth: number,
  viewportHeight: number,
  zoom: number,
): WorldRect {
  const width = viewportWidth / zoom;
  const height = viewportHeight / zoom;

  return {
    x: scrollX + viewportWidth / 2 - width / 2,
    y: scrollY + viewportHeight / 2 - height / 2,
    width,
    height,
  };
}

/**
 * Top-left viewport UV for a world point in `view`. The fog overlay is a quad that *is* this
 * rectangle, so these UVs are the same coordinates the fragment shader reconstructs from.
 */
export function worldToViewUv(
  worldX: number,
  worldY: number,
  view: WorldRect,
): { x: number; y: number } {
  return {
    x: (worldX - view.x) / view.width,
    y: (worldY - view.y) / view.height,
  };
}

/** How to pin the map when the view is wider than the bounds. */
export type ConstrainFit = 'center' | 'overlap';

export type ConstrainScrollOptions = {
  fit?: ConstrainFit;
};

/**
 * Scroll that keeps the world point now under `screenPx` under that same pixel after a zoom change.
 *
 * Phaser's `getWorldPoint` / `worldView` are only written in `preRender`, so a `setZoom` then
 * `getWorldPoint` pair still uses last frame's matrix and looks like a centre zoom.
 */
export function scrollToKeepWorldPoint(
  scroll: number,
  viewportPx: number,
  oldZoom: number,
  newZoom: number,
  screenPx: number,
): number {
  return scroll + (screenPx - viewportPx / 2) * (1 / oldZoom - 1 / newZoom);
}

/**
 * Scroll for one axis: clamped to the map edges while the map is larger than the view.
 *
 * `fit: 'center'` (default, play) recentres once the view is wider than the bounds.
 * `fit: 'overlap'` (editor) only keeps the map intersecting the view, so pointer-anchored zoom
 * and Space-pan can sit the map off-centre.
 *
 * This replaces `Camera.setBounds` because Phaser's own clamp pins a map smaller than the
 * viewport to the top-left corner, which is exactly the case the deep zoom-out creates.
 */
export function constrainScroll(
  scroll: number,
  viewportPx: number,
  zoom: number,
  boundsSize: number,
  options: ConstrainScrollOptions = {},
): number {
  const view = viewportPx / zoom;
  const half = viewportPx / 2;
  const fit = options.fit ?? 'center';

  if (view >= boundsSize) {
    if (fit === 'center') {
      return boundsSize / 2 - half;
    }

    // World left is `scroll + half - view / 2`. Keep [0, bounds] overlapping that window.
    const min = -half - view / 2;
    const max = boundsSize - half + view / 2;
    return Math.min(Math.max(scroll, min), max);
  }

  // Phaser zooms about `scroll + viewportPx / 2`, so that is the world point held steady here.
  const center = Math.min(Math.max(scroll + half, view / 2), boundsSize - view / 2);
  return center - half;
}
